export const Canine_Fundamentals = {
  dogDevelopmentStages: {
    title: "Developmental Stages of Dogs",
    image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/dogDevelopmentStagesTitle.png",
    body: `
      <section>
        <h3>🐾 Growth Happens in Stages — So Should Our Approach</h3>
        <p>
          At Pawsible, we recognize that <span class="highlight">dogs change dramatically through developmental stages</span>. 
          Understanding these phases allows us to <span class="highlight-green">adjust our care, training, and expectations</span> to best support every dog at every age.
        </p>
      </section>
  
      <section>
        <h4>🍼 1. Neonatal Stage (0–2 Weeks)</h4>
        <ul>
          <li>🐣 Eyes and ears are closed; puppies rely entirely on smell and touch</li>
          <li>🛏️ No mobility or social interaction yet — 100% dependent on mother</li>
          <li>🏥 Not applicable for daycare or training environments</li>
        </ul>
      </section>
  
      <section>
        <h4>👀 2. Transitional Stage (2–4 Weeks)</h4>
        <ul>
          <li>👁️ Eyes and ears begin to open; first steps toward sensory awareness</li>
          <li>🎯 Start of very basic movement (wobbling, crawling)</li>
          <li>🏥 Still not applicable for daycare; remain in breeder or foster care settings</li>
        </ul>
      </section>
  
      <section>
        <h4>🐾 3. Socialization Stage (3–12 Weeks)</h4>
        <ul>
          <li>🌍 Puppies begin exploring their world rapidly and imprinting on experiences</li>
          <li>🐕 Critical period for socializing with dogs, people, sounds, textures, and handling</li>
          <li>⚠️ Negative experiences can create lifelong fears — gentle, positive exposure is essential</li>
          <li>🧠 Start of basic learning and soft shaping (name response, coming when called)</li>
        </ul>
        <p><span class="highlight-blue">At Pawsible: Puppies entering daycare must be fully vaccinated per age and treated with extra patience, positive exposure, and confidence-building activities.</span></p>
      </section>
  
      <section>
        <h4>🚀 4. Juvenile Stage (3–6 Months)</h4>
        <ul>
          <li>🏃 Increased independence and curiosity; higher energy levels</li>
          <li>🧩 Need structured learning: gentle boundaries, basic cues, positive play experiences</li>
          <li>💬 May test limits — normal "teenager" behavior emerging</li>
          <li>🐾 Physical growth surges — clumsy movements and sensitive joints common</li>
        </ul>
      </section>
  
      <section>
        <h4>🔥 5. Adolescent Stage (6–24 Months)</h4>
        <ul>
          <li>😈 Hormonal shifts impact behavior: increased confidence, testing, or insecurity</li>
          <li>🏋️‍♂️ Need clear but fair leadership — consistency matters more than intensity</li>
          <li>⚡ High physical energy; risk of rougher play or boundary-pushing behavior</li>
          <li>🤝 Crucial time to reinforce social skills, emotional regulation, and respectful play habits</li>
        </ul>
        <p><span class="highlight-green">At Pawsible: Adolescents require active supervision, enrichment, and regular calm breaks to prevent overstimulation or conflict.</span></p>
      </section>
  
      <section>
        <h4>🎯 6. Young Adult Stage (2–5 Years)</h4>
        <ul>
          <li>🧘 Dogs mature emotionally and physically — behaviors become more stable</li>
          <li>🌟 Peak physical fitness and play ability; great candidates for structured group play</li>
          <li>🛡️ Ongoing reinforcement of social manners still important</li>
          <li>🧠 Enjoy mentally stimulating challenges (advanced enrichment, trick training)</li>
        </ul>
      </section>
  
      <section>
        <h4>🏡 7. Mature Adult Stage (5–8 Years)</h4>
        <ul>
          <li>💤 May begin showing preference for calmer play or smaller groups</li>
          <li>💬 Communication becomes clearer — dogs know their social preferences well</li>
          <li>🩺 Monitor for early signs of arthritis, dental issues, or energy level changes</li>
        </ul>
        <p><span class="highlight-blue">At Pawsible: Mature adults thrive with a balance of structured play, enrichment, and choice-based rest opportunities.</span></p>
      </section>
  
      <section>
        <h4>🧓 8. Senior Stage (8+ Years)</h4>
        <ul>
          <li>🌙 Slower pace, reduced stamina — prefer calm companionship and sensory enrichment</li>
          <li>🛏️ Provide frequent rest opportunities and easy access to shaded or quiet areas</li>
          <li>🩺 Watch closely for health issues: arthritis, vision changes, reduced hearing</li>
          <li>🤝 Gentle handling, soft enrichment activities, and flexible scheduling are key</li>
        </ul>
      </section>
  
      <section>
        <h4>🌱 Final Thought</h4>
        <p>
          <span class="highlight">Dogs don’t just grow physically — they grow emotionally, socially, and mentally at every stage.</span> 
          At Pawsible, honoring each developmental phase allows us to provide smarter, more compassionate care that evolves with every dog we serve.
        </p>
      </section>
    `
  },    
  canineBehavior: {
        title: "Understanding Canine Behavior",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/canineBehaviorTitle.png",
        body: `
        <section>
            <h3>🐶 Why This Matters</h3>
            <p><span class="highlight">Dogs don't speak our language</span> — they speak body. At Pawsible, recognizing canine signals is essential to build trust and prevent stress or injury. It's how we <span class="highlight">read before we reach</span>.</p>
        </section>
      
        <section>
            <h4>💡 Key Signs to Watch:</h4>
            <ul>
                <li><strong>😊 Relaxed posture</strong>: Soft eyes, loose tail wag, mouth slightly open, body gently curved. = <span class="highlight-green">Comfortable</span></li>
                <li><strong>😌 Play bow</strong>: Front legs stretched forward, rear up, wagging tail. = <span class="highlight-green">Inviting play</span></li>
                <li><strong>😬 Lip licking / Yawning</strong>: Subtle appeasement behaviors when overwhelmed. = <span class="highlight-yellow">Anxious or stressed</span></li>
                <li><strong>😟 Tucked tail / Crouched body</strong>: Ears back, avoiding eye contact, weight shifted away. = <span class="highlight-yellow">Fearful or insecure</span></li>
                <li><strong>😠 Stiff body</strong>: Fixed stare, tense muscles, closed mouth, tail high and rigid. = <span class="highlight-red">Potential aggression</span></li>
                <li><strong>🚩 Whale eye</strong>: White of the eye visible, head turned but eyes locked. = <span class="highlight-red">High-stress alert</span></li>
            </ul>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/canineBehaviorPosture.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
        </section>

        <section>
            <h4>🎓 Staff Tips</h4>
            <ul>
            <li>Approach from the side or with a soft arc</li>
            <li>Avoid leaning over or direct eye contact initially</li>
            <li>Give dogs time to <span class="highlight">choose engagement</span></li>
            <li>Always use a <span class="highlight">treat bridge</span> (food lure) when unsure</li>
            </ul>
        </section>

        <section>
            <h4>⚠️ Safety Reminder</h4>
            <p><span class="highlight">Always prioritize your safety.</span> If a dog exhibits stiff posture, avoidance, or growling, <span class="highlight-red"><strong>do not proceed</strong></span>. Alert a supervisor or trainer. Never attempt to handle a dog that shows signs of potential aggression alone.</p>
            <ul>
                <li>Use barriers or leashes for protection if needed</li>
                <li>Call for backup if uncertain</li>
                <li>Remain calm and still — avoid sudden movements</li>
            </ul>
        </section>
      
        <section>
            <h4>👀 Real-Life Scenario</h4>
            <p><em>Luna, a rescue shepherd, shows a tight body and lip licking when approached. Instead of reaching over her head, staff kneels sideways, offers a treat at ground level, and lets her sniff. Luna slowly relaxes and starts wagging. She now trusts and willingly follows on leash.</em></p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/canineBehaviorRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
        </section>
        
        <section>
            <h4>🧠 Takeaway</h4>
            <p><span class="highlight">Behavior is a window into emotion.</span> Learning to observe and interpret it is the first step in excellent care. When in doubt: slow down, offer space, and observe.</p>
        </section>`
      },
      learningTheory: {
        title: "Learning Theory in Practice",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/learningTheoryTitle.png",
        body: `
            <section>
                <h3>📘 Why This Matters</h3>
                <p>At Pawsible, <span class="highlight">we train through connection, not correction</span>. By using positive reinforcement-based methods, we create trust, improve safety, and build lasting behaviors.</p>
            </section>

            <section>
                <h4>🔁 Operant Conditioning</h4>
                <p>We use this every day. Dogs learn that <span class="highlight">what they do leads to what they get</span>.</p>
                <ul>
                    <li><strong>✅ Positive Reinforcement</strong>: Give a treat <em>after</em> a sit → Sitting increases</li>
                    <li><strong>❌ Negative Punishment</strong>: Turn away <em>after</em> a jump → Jumping decreases</li>
                    <li><span class="highlight-red">We do not use force, fear, or intimidation. Ever.</span></li>
                </ul>
                <div class="embedded-visual">
                    <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/learningTheoryOperant.png" alt="Real-Life Incident Example" class="inline-guide-image" />
                </div>
                <p>In essence, operant conditioning is about learning <span class="highlight" >consequences</span>. The dog learns that the action of sitting results in getting a treat, so they are more likely to sit when asked.</p>
            </section>

            <section>
                <h4>🧪 Classical Conditioning</h4>
                <p>This is about pairing good experiences with neutral things. It's how dogs learn to <span class="highlight">feel safe</span>.</p>
                <ul class="decorated-conditioning-list">
                    <li>
                        <span class="condition-cue">🧼 Grooming Table</span> → 
                        <span class="condition-effect highlight-green">Calm, happy vibes</span> 🦴
                    </li>
                    <li>
                        <span class="condition-cue">🐾 Staff Kindness</span> → 
                        <span class="condition-effect highlight-blue">Trust in people</span> 💙
                    </li>
                    <li>
                        <span class="condition-cue">🛏️ Crate Time</span> → 
                        <span class="condition-effect highlight-yellow">Relaxation & safety</span> 😴
                    </li>
                </ul>
                <div class="embedded-visual">
                    <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/learningTheoryClassical.png" alt="Real-Life Incident Example" class="inline-guide-image" />
                </div>
                <p>In essence, classical conditioning is about learning <span class="highlight" >signals</span>. The dog learns that the sound of the treat bag signals that a treat is coming, so they might get excited.</p>
            </section>
      
            <section>
                <h4>🔊 Marker Words & Timing</h4>
                <p>Use <strong class="highlight">“Yes!”</strong> or a clicker to mark success <span class="highlight">immediately</span>. Follow with a reward <span class="highlight">within 1 second</span>.</p>
                <ul>
                <li>Consistency = clarity for the dog</li>
                <li>Marker first, reward second — always in that order</li>
                </ul>
            </section>
        
            <section>
                <h4>🎓 Staff Tips</h4>
                <ul>
                <li>Use calm voice and confident posture while training</li>
                <li>Keep sessions short (5–7 mins) and frequent</li>
                <li>Practice in low-distraction areas first</li>
                <li>Reinforce calm, not just commands — <span class="highlight">reward the state of mind you want more of</span></li>
                </ul>
            </section>
        
            <section>
                <h4>👀 Real-Life Scenario</h4>
                <p><em>Milo, a young doodle, jumps during leash clipping. Instead of saying "No," the staff rewards him for standing still. They say “Yes!” the moment he is calm, treat him, then clip. Milo learns that standing still brings rewards — not pressure.</em></p>
                <div class="embedded-visual">
                    <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/learningTheoryRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
                </div>
            </section>
        
            <section>
                <h4>🌱 Takeaway</h4>
                <p><span class="highlight">Training is not about control — it's about communication.</span> When dogs understand what earns rewards, they engage willingly. Our job is to make the right choice the easiest one for them.</p>
            </section>`
      },      
      safeHandling: {
        title: "Safe Handling Techniques",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safeHandlingTitle.png",
        body: `
          <section>
            <h3>🛡️ Why This Matters</h3>
            <p><span class="highlight">Handling is the first moment of trust — or fear.</span> Every touch teaches. At Pawsible, we lead with calm confidence, not force. Safety begins with awareness of the dog's body language and your own movements.</p>
          </section>
      
          <section>
            <h4>✅ Safe Handling Basics</h4>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safeHandlingBasics.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            <ul>
              <li>Approach <span class="highlight">from the side</span>, not from above</li>
              <li>Use a <span class="highlight">neutral, calm tone</span> — avoid baby talk or sudden volume</li>
              <li>Let the dog sniff first if unsure</li>
              <li><span class="highlight">Stabilize without pinning</span> — confident holds build safety, not fear</li>
              <li>Slip leads: secure snugly <em>behind ears</em>, not dangling or choking</li>
            </ul>
          </section>
      
          <section>
            <h4>🧠 Real-Life Scenario</h4>
            <p><em>A high-energy doodle bounces into the shop. Instead of reaching or restraining, staff stands still, lets the dog approach, then offers a treat. Once the dog sits calmly, they gently clip on the leash. Result: smooth transition, no struggle.</em></p>
            <div class="embedded-visual">
                <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safeHandlingRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
            </div>
          </section>
      
          <section>
            <h4>🛑 Red Flags: When to Immediately Stop</h4>
            <p><span class="highlight-red">If you observe any of the following, do not proceed and immediately inform a supervisor or trainer:</span></p>
            <ul>
                <li>Resisting</li>
                <li>Growling</li>
                <li>Snapping or air snapping</li>
                <li>Hard, direct stare</li>
                <li>Stiffening of the body (Hackles - erectile hairs)</li>
                <li>Showing the whites of the eyes (whale eye) along with tension</li>
                <li>Attempting to bite</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safeHandlingRedFlag.jpeg" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🌱 Takeaway</h4>
            <p><span class="highlight">Control comes from calm presence, not force.</span> Handling is a shared experience — we build trust with every touch, leash, and pause.</p>
          </section>
        `
    },      
    obedienceCueIntro: {
        title: "Obedience Training & Cue Introduction",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/obedienceTrainingTitle.png",
        body: `
          <section>
            <h3>🎓 Why This Matters</h3>
            <p>Obedience cues create <span class="highlight">structure</span>, support <span class="highlight">impulse control</span>, and <span class="highlight">empower dogs to succeed</span> in a social setting. Every cue is an <span class="highlight-green">agreement</span> — not a demand. It builds <span class="highlight-green">trust</span> and helps dogs thrive at Pawsible and beyond.</p>
          </section>
      
          <section>
            <h4>📋 Core Cues We Teach</h4>
            <ul>
              <li><strong>"Sit" 🪑</strong> – A default <span class="highlight-green">polite behavior</span> before doors, leashing, or greetings</li>
              <li><strong>"Down" 🛏️</strong> – Promotes <span class="highlight-green">calm, stillness, and relaxation</span></li>
              <li><strong>"Stay" ⏸️</strong> – Builds <span class="highlight-green">impulse control</span> and <span class="highlight-green">predictability</span></li>
              <li><strong>"Come" 🏃</strong> – <span class="highlight-red">Essential recall cue</span> during transitions and emergencies</li>
              <li><strong>"Leave it" 🚫</strong> – <span class="highlight-red">Interrupts unwanted fixation</span> or unsafe focus</li>
              <li><strong>"Place" 🎯</strong> – Sends dog to a <span class="highlight-green">designated calming zone</span> (e.g., cot)</li>
            </ul>
          </section>
      
          <section>
            <h4>✨ Teaching Flow</h4>
            <ul>
              <li><strong>Step 1: <span class="highlight-blue">Cue</span></strong> — Say the word (e.g., “Sit”)</li>
              <li><strong>Step 2: <span class="highlight-blue">Mark</span></strong> — Use “Yes!” the moment behavior is correct</li>
              <li><strong>Step 3: <span class="highlight-blue">Reward</span></strong> — Deliver treat, praise, or affection <span class="highlight">within 1 second</span></li>
              <li><strong>Step 4: <span class="highlight-blue">Repeat</span></strong> — Practice in short, upbeat sessions (3–5 mins)</li>
            </ul>
          </section>
      
          <section>
            <h4>🔄 What is DRA?</h4>
            <p><strong>Differential Reinforcement of Alternative behavior (DRA)</strong> means reinforcing a <span class="highlight-blue">desired alternative behavior</span> instead of reacting to the undesired one. It teaches dogs <span class="highlight">what to do</span>, not just what to avoid.</p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/obedienceTrainingDRA.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>💡 Cue-Based Redirection</h4>
            <p>Replace challenges with trained behaviors:</p>
            <ul>
              <li><strong>Jumping</strong> ➝ Cue <strong>“Sit”</strong> ➝ <span class="highlight-green">Reward sit</span></li>
              <li><strong>Barking for attention</strong> ➝ Cue <strong>“Down”</strong> ➝ <span class="highlight-green">Reward calm</span></li>
              <li><strong>Leash pulling</strong> ➝ Cue <strong>“Sit” or “Stay”</strong> ➝ <span class="highlight-green">Reward slack leash</span></li>
              <li><strong>Mouthing</strong> ➝ Redirect to toy ➝ Cue <strong>“Leave it”</strong> ➝ <span class="highlight-green">Reward redirection</span></li>
              <li><strong>Door rushing</strong> ➝ Cue <strong>“Sit” or “Stay”</strong> ➝ <span class="highlight-green">Reward waiting</span></li>
              <li><strong>Overstimulation</strong> ➝ Cue <strong>“Place”</strong> ➝ <span class="highlight-green">Reward calm placement</span></li>
            </ul>
          </section>
      
          <section>
            <h4>👀 Real-Life Scenario</h4>
            <p><em>Kobe, a high-energy pup, gets overstimulated during group play. Staff cues “Place” and guides him to a cot. Once he lays down, they mark “Yes!” and reward with calm praise. In 2 minutes, he’s calmer and tuned in.</em></p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/obedienceTrainingRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🎯 Staff Tips</h4>
            <ul>
              <li><span class="highlight">Be consistent</span> — always use the same cues</li>
              <li><span class="highlight">Train in calm settings first</span> — then layer in distractions</li>
              <li><span class="highlight">Reinforce small wins</span> — don’t wait for perfection</li>
              <li><span class="highlight-red">End every session on a success</span> — let dogs win!</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Takeaway</h4>
            <p><span class="highlight">Cues are collaborative tools</span>, not commands. When used <span class="highlight-green">with kindness, clarity, and consistency</span>, they create mutual understanding and emotional safety.</p>
          </section>`
    },
    behaviorShaping: {
        title: "Reinforcement & Behavior Shaping",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/behaviorShapingTitl.png",
        body: `
          <section>
            <h3>🧱 Why It Matters</h3>
            <p><span class="highlight">Shaping is how we build success through tiny wins.</span> It’s ideal for teaching complex, new, or scary tasks — like grooming tolerance or stationing. Every step forward gets a reward. We build confidence, not compliance.</p>
          </section>
      
          <section>
            <h4>🔍 Shaping vs Luring vs Capturing</h4>
            <ul>
              <li><strong>Shaping</strong>: Rewarding small steps toward a goal (ideal for fear or finesse)</li>
              <li><strong>Luring</strong>: Using a treat or hand to guide the dog’s body</li>
              <li><strong>Capturing</strong>: Waiting for a natural behavior, then reinforcing it (e.g., lying down)</li>
            </ul>
            <p>✨ <span class="highlight">Use shaping when a dog is unsure or needs confidence-building.</span></p>
          </section>
      
          <section>
            <h4>⏳ Timing & Reinforcement Rate</h4>
            <ul>
              <li>⏱️ <strong>Mark and reward within 1–2 seconds</strong> of the behavior</li>
              <li>🔁 If the dog struggles, go <span class="highlight">back one step</span> to build success</li>
              <li>📏 Keep shaping sessions short — <span class="highlight">3–5 minutes max</span></li>
            </ul>
            <p>🎯 Quick, clear feedback creates faster learning and more enthusiasm.</p>
          </section>
      
          <section>
            <h4>🔄 Resetting When the Dog Struggles</h4>
            <ul>
              <li>❌ Stop if the dog shows stress or confusion</li>
              <li>⬅️ Reduce criteria (e.g., reward for just offering the paw again)</li>
              <li>🔔 Use a reset word like “All done!” or “Try again” to pause cleanly</li>
            </ul>
            <p><span class="highlight-red">Always end on a success — even if it's small.</span></p>
          </section>
      
          <section>
            <h4>💰 Know Their Currency</h4>
            <p>Each dog has a different motivator. Use what they love:</p>
            <ul>
              <li>🍖 High-value treats</li>
              <li>🎾 Favorite toys or games</li>
              <li>🥰 Praise or gentle petting</li>
              <li>🚶 Access to play or walks</li>
            </ul>
          </section>
      
          <section>
            <h4>👀 Real-Life Scenario</h4>
            <p><em>Maple is afraid of nail trims. Instead of forcing, staff rewards her for touching a nail file with her paw. Over time, she allows light holding, then tool contact — finally, one quick clip with a treat. Maple now approaches calmly for trims.</em></p>
            <p>🎯 Step-by-Step: Nail Trim Tolerance</p>
            <ul class="shaping-sequence-list">
              <li>✅ Dog offers paw ➝ reward</li>
              <li>✅ Dog allows gentle hold ➝ reward</li>
              <li>✅ Touch paw with nail tool ➝ reward</li>
              <li>✅ Quick clip ➝ BIG praise + reward!</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/behaviorShapingRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🌱 Takeaway</h4>
            <p><span class="highlight">Shaping is about progress, not perfection.</span> By rewarding effort and breaking behaviors into tiny wins, we help dogs feel proud — and safe — to learn with us.</p>
          </section>
        `
    },      
    safetyEnvironment: {
        title: "Safety Protocols & Environment Setup",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safetyEnvironmentTitle.png",
        body: `
          <section>
            <h3>🛡️ Why This Matters</h3>
            <p><span class="highlight">Safety is a mindset, not just a checklist.</span> Every room, zone, and decision at Pawsible is intentionally designed to prevent injury, reduce stress, and support positive learning experiences.</p>
          </section>
      
          <section>
            <h4>🏘️ The Ideal Environment</h4>
            <ul>
              <li>🧼 <strong>Clean</strong>: Sanitize bowls, bedding, and floors <span class="highlight">every day — no exceptions</span></li>
              <li>🧩 <strong>Structured</strong>: Separate high-energy areas from quiet zones to <span class="highlight">match each dog's needs</span></li>
              <li>👁️ <strong>Supervised</strong>: <span class="highlight-red">Never leave dogs unattended.</span> Eyes on, always.</li>
            </ul>
          </section>
      
          <section>
            <h4>🐕 Playgroup Sorting</h4>
            <ul>
              <li>📏 <strong>Size</strong>: Tiny dogs shouldn't mix with large ones unless matched by <span class="highlight-blue">compatible energy</span></li>
              <li>🧠 <strong>Temperament</strong>: Watch for <span class="highlight-yellow">fear, overexcitement, or overstimulation</span> — then adjust</li>
              <li>🎮 <strong>Play Style</strong>: Chasers, wrestlers, shy pups — <span class="highlight">every dog needs the right match</span></li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safetyEnvironmentSorting.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🧰 Tools & Prevention Tactics</h4>
            <ul>
              <li>🔲 <strong>Visual Barriers</strong>: Use to break eye contact or offer decompression space</li>
              <li>🪢 <strong>Leashes Nearby</strong>: Always within reach — <span class="highlight">hooked or with handler</span></li>
              <li>👏💧 <strong>Interrupt Tools</strong>: One clap, a spritz of water, or a calm body block — <span class="highlight-red">never yelling or chasing</span></li>
              <li>🗂️ <strong>Incident Logs</strong>: Record even minor incidents — <span class="highlight-yellow">small warnings prevent big problems</span></li>
            </ul>
          </section>
      
          <section>
            <h4>👀 Real-Life Scenario</h4>
            <p><em>Charlie starts humping mid-play. A team member calmly claps once and calls, “Charlie, let’s go!” He’s guided to a calm zone. <span class="highlight-green">No scolding, no chaos.</span> The moment is logged, the owner is informed with empathy, and the team regroups to adjust group dynamics.</em></p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/safetyEnvironmentRealLife.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🌱 Takeaway</h4>
            <p><span class="highlight-blue">Prevent first, then redirect calmly.</span> Your tone, body language, and the environment you set determine whether dogs feel safe — and stay safe — at Pawsible.</p>
          </section>
        `
    },       
    dogEmergencies: {
        title: "Canine Emergency Response Protocols",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emergencyProtocolsTitle.png",
        body: `
          <section>
            <h3>🚨 Stay Calm, Stay in Control</h3>
            <p><span class="highlight">Emergencies don’t ask for permission — they just happen.</span> Your job is to stay calm, act fast, and follow protocol. Dogs mirror our energy, so your steady response makes all the difference.</p>
          </section>
      
          <section>
            <h4>🩹 First Aid & Injury Response</h4>
            <ul>
              <li>🛑 <strong>Stop play immediately</strong> if blood, limping, yelping, or unusual behavior is observed</li>
              <li>📍 <strong>Secure the area</strong> — calmly separate nearby dogs to prevent crowding or escalation</li>
              <li>🧯 <strong>Stabilize</strong> with basic first aid: stop bleeding with gauze, apply cold compress for swelling</li>
              <li>📞 <strong>Notify supervisor</strong> immediately for further direction and documentation</li>
            </ul>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emergencyProtocolsAid.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🚪 Escape or Elopement</h4>
            <ul>
              <li>🔐 <strong>Alert the team</strong> via radio or shout key phrase ("Dog Out from Daycare!")</li>
              <li>🚷 <strong>Block exits</strong> — front door, back gates, side yards</li>
              <li>🏃‍♀️ <strong>Mobilize search</strong> — staff go in pairs if outside the premises</li>
              <li>📲 <strong>Inform owner + log incident</strong> once dog is secured</li>
            </ul>
          </section>
      
          <section>
            <h4>😮‍💨 Breathing, Collapse, or Seizure</h4>
            <ul>
              <li>🔎 <strong>Observe symptoms</strong> — tongue color, body stiffness, eye movement</li>
              <li>📦 <strong>Create space</strong> — remove dogs, reduce stimulation, pad surroundings</li>
              <li>🐾 <strong>Do not restrain mouth</strong> or hold dog down</li>
              <li>📞 <strong>Call supervisor and vet partner immediately</strong></li>
            </ul>
          </section>
      
          <section>
            <h4>👥 Notifying the Owner</h4>
            <p><span class="highlight-blue">Transparency builds trust.</span> If an incident occurs — even minor — the pet parent must be informed with empathy and clarity. Keep it calm, professional, and solutions-focused.</p>
            <img loading="lazy" src="https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/emergencyProtocolsRole.png" alt="Real-Life Incident Example" class="inline-guide-image" />
          </section>
      
          <section>
            <h4>🗂️ Logging the Incident</h4>
            <p>All emergencies must be <span class="highlight-yellow">documented in the Incident Log</span> including:</p>
            <ul>
              <li>Time, location, and team members present</li>
              <li>Dog(s) involved and what occurred</li>
              <li>Actions taken and next steps</li>
            </ul>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p><span class="highlight-green">The goal isn’t just fast action — it’s calm, confident care.</span> Protocols exist to keep everyone safe and to give pet parents peace of mind that their dog is in capable hands.</p>
          </section>
        `
    }
};